import { ValidationRule } from 'react-hook-form'
import { TFunction } from 'i18next'

const validationMessage = {
  required: (t: TFunction): string => t('joi.any.required'),
  notEmpty: (t: TFunction): string => t('joi.string.empty'),
  minLength: (limit: number, t: TFunction): string => t('joi.string.min', '', { limit }).replace('{#limit}', `${limit}`),
  maxLength: (limit: number, t: TFunction): string => t('joi.string.min', '', { limit }).replace('{#limit}', `${limit}`),
  min: (limit: number, t: TFunction): string => t('joi.number.min', '', { limit }).replace('{#limit}', `${limit}`),
  max: (limit: number, t: TFunction): string => t('joi.number.max', '', { limit }).replace('{#limit}', `${limit}`),
}

/**
 * Validation rules for react-hook-forms in function form for convenience.
 * It uses common validation translations.
 */
export const validation = {
  required: (t: TFunction): ValidationRule<boolean> => ({
    value: true,
    message: validationMessage.required(t),
  }),
  /** String, non empty */
  notEmpty: (t: TFunction): ValidationRule<boolean> => ({
    value: true,
    message: validationMessage.notEmpty(t),
  }),
  /** String length minimun */
  minLength: (limit: number, t: TFunction): ValidationRule<number> => ({
    value: limit,
    message: validationMessage.minLength(limit, t),
  }),
  /** String length maximum */
  maxLength: (limit: number, t: TFunction): ValidationRule<number> => ({
    value: limit,
    message: validationMessage.maxLength(limit, t),
  }),
  /** Number minimun */
  min: (limit: number, t: TFunction): ValidationRule<number> => ({
    value: limit,
    message: validationMessage.min(limit, t),
  }),
  /** Number maximum */
  max: (limit: number, t: TFunction): ValidationRule<number> => ({
    value: limit,
    message: validationMessage.max(limit, t),
  }),
}

/**
 * Regex that accepts all valid ascii characters. A-Z, a-z, 0-9, special character and whitespaces.
 *
 * It also accepts some unicode characters that are used by input components to prevent edge cases:
 * - all unicode whitespaces
 * - U+200C = ZERO WIDTH NON-JOINER (ZWNJ)
 * - U+200D = ZERO WIDTH JOINER (ZWJ)
 * - All special characters of supported languages.
 *
 * It is used to prevent characters from not supported languages in strings like: Ά Ω 你
 */
export const asciiRegex =
  // eslint-disable-next-line no-control-regex, no-misleading-character-class
  /^([\s\u0000-\u007F\u200C\u200DßéÉèÈêÊëËâÂûÛîÎôÔàÀùÙçÇÄÖÜäöüáíóúñÑÁÃÍÓÕÚÅåÆÏŒŸæïœÿzZÌÒìòãõ])*$/u

export const asciiPattern = (t: TFunction): ValidationRule<RegExp> => {
  const pattern: ValidationRule<RegExp> = {
    value: asciiRegex,
    message: t('joi.ascii'),
    unicode: true,
  }

  return pattern
}
